import { Platform } from '@angular/cdk/platform'
import { AfterViewInit, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { DateAdapter } from '@angular/material/core'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatDialog } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { YhteisomyyntilaskujenCsvVastaus, YhteisomyyntilaskujenListauspyynto } from '@lemon/core-firebase/lemonator/model/tositteiden-lataaminen'
import { DateService } from '@lemon/core/service/date.service'
import { OmaveroYhteisomyyntilaskuLahetetty } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/omavero'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { AnnaAsiakkaidenNimitiedotPyynto, AnnaAsiakkaidenNimitiedotVastaus, AsiakkaanNimitiedot } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { Observable, Subject, combineLatest, map, of, startWith, switchMap, takeUntil, tap } from 'rxjs'
import { LemonKuukausiDateAdapter } from '../_jaettu-angular/_material/LemonKuukausiDateAdapter'
import { YhteisomyyntilaskujenLataaminenDialog, YhteisomyyntilaskujenLataaminenDialogData } from './yhteisomyyntilaskujen-lataaminen.dialog'

interface OmaveroYhteisomyyntilaskuLahetettyList extends OmaveroYhteisomyyntilaskuLahetetty {
  asiakas: string
  debugLink: string
}

interface KuukausiLomake {
  vuosikk: Date | null
}

interface YhteisomyyntiIlmoituksetLomake {
  status: string
  start: Date | null
  end: Date | null
}

@Component({
  templateUrl: './yhteisomyyntilaskut.component.html',
  styleUrls: ['./yhteisomyyntilaskut.component.css'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class YhteisomyyntilaskutComponent implements OnInit, OnDestroy, AfterViewInit {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  readonly kuukausi = signal<Date>(new Date())
  lomake: FieldTree<KuukausiLomake>
  hakulomake: FieldTree<YhteisomyyntiIlmoituksetLomake>
  private readonly _injector = inject(Injector)
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  readonly naytettavatKolumnit = signal<string[]>(['asiakasNimi', 'jakso', 'maaKoodi', 'alvTunnus', 'tavaramyynnit', 'palvelumyynnit', 'kolmikantamyynnit', 'lahetetty', 'status'])
  dataSource = new MatTableDataSource<OmaveroYhteisomyyntilaskuLahetettyList>([])
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  readonly kuukaudenNimi = signal('')
  lahetyksenTila: { status: string }[] = [{ status: 'Onnistuneet' }, { status: 'Virheelliset' }, { status: 'Kaikki statukset' }]

  constructor(
    private _errorHandler: ErrorHandler,
    private _dateService: DateService,
    private _dateAdapter: DateAdapter<Date>,
    private _dialog: MatDialog,
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _fileSaverService: FileSaverService,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _kirjanpitoUriService: KirjanpitoUriService,
  ) {
    this.kuukausi.set(this._dateService.lisaaKuukausia(new Date(), -1))
    this.kuukaudenNimi.set(this._dateAdapter.getMonthNames('long')[this.kuukausi().getMonth()])
  }

  ngOnInit() {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe((onDevaaja: boolean) => {
      if (onDevaaja) {
        this.naytettavatKolumnit.set([...this.naytettavatKolumnit(), 'debug'])
      }
    })

    const injector = this._injector
    this.lomake = form(
      signal<KuukausiLomake>({ vuosikk: this.kuukausi() }),
      polku => {
        required(polku.vuosikk)
      },
      { injector },
    )
    arvonMuutokset(this.lomake.vuosikk, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: Date) => {
        if (this.lomake.vuosikk().valid()) {
          this.kuukausi.set(value)
          this.kuukaudenNimi.set(this._dateAdapter.getMonthNames('long')[this.kuukausi().getMonth()])
        }
      })

    const today = new Date()
    this.hakulomake = form(
      signal<YhteisomyyntiIlmoituksetLomake>({ status: '', start: this._dateService.lisaaKuukausia(today, -1), end: this._dateService.lisaaKuukausia(today, -1) }),
      polku => {
        required(polku.start)
        required(polku.end)
      },
      { injector },
    )

    const start = this.hakulomake.start
    const end = this.hakulomake.end
    const status = this.hakulomake.status

    const statukset = {
      Onnistuneet: ['success'],
      Virheelliset: ['error'],
      'Kaikki statukset': ['success', 'error'],
    }

    const asiakkaatMapObservable = this._firebaseLemonator
      .functionsCall<AnnaAsiakkaidenNimitiedotPyynto, AnnaAsiakkaidenNimitiedotVastaus>('annaAsiakkaidenNimitiedot', {}, { timeout: 540 * 1000 })
      .then(asiakkaatVastaus => {
        const m: Map<string, AsiakkaanNimitiedot> = new Map()
        if (asiakkaatVastaus?.nimitiedot) {
          for (const a of asiakkaatVastaus.nimitiedot) {
            m.set(a.avain, a)
          }
        }
        return m
      })

    const ilmoituksetObservable = combineLatest([
      arvonMuutokset(start, { injector }).pipe(startWith(start().value())),
      arvonMuutokset(end, { injector }).pipe(startWith(end().value())),
      arvonMuutokset(status, { injector }).pipe(startWith(status().value())),
    ]).pipe(
      tap(() => this._ladataanService.aloitaLataaminen()),
      switchMap(([startValue, endValue, statusValue]) => {
        if (!startValue || !endValue || !statusValue) {
          return of<OmaveroYhteisomyyntilaskuLahetetty[]>([])
        }
        const startNumberMonth = this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(startValue))
        const endNumberMonth = this._dateService.numberDateToNumberMonth(this._dateService.dateToNumber(endValue))

        return this._firebaseLemonator
          .firestoreCollectionGroup<OmaveroYhteisomyyntilaskuLahetetty>('yhteisomyynti-ilmoitukset')
          .orderBy('lahetetty', 'desc')
          .where('status', 'in', statukset[statusValue])
          .where('jakso', '>=', startNumberMonth)
          .where('jakso', '<=', endNumberMonth)
          .listen()
      }),
    )

    combineLatest([ilmoituksetObservable, asiakkaatMapObservable])
      .pipe(
        map(([ilmot, asiakkaatMap]) => {
          const result: OmaveroYhteisomyyntilaskuLahetettyList[] = ilmot as OmaveroYhteisomyyntilaskuLahetettyList[]
          for (const res of result) {
            const asiakas = asiakkaatMap.get(res.asiakasAvain)
            if (asiakas) {
              res.asiakas = asiakas.nimi + ' (' + asiakas.ytunnus + ')'
            } else {
              res.asiakas = asiakas.avain
            }
            res.debugLink = this._debugService.createFirestoreLink(this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyDebugDataUri(res.asiakasAvain, res.lahetysAvain))
          }
          // Sorttaus Y-tunnuksen mukaan
          result.sort((a, b) => {
            const asiakasA = asiakkaatMap.get(a.asiakasAvain)
            const asiakasB = asiakkaatMap.get(b.asiakasAvain)

            if (asiakasA && asiakasB) {
              return asiakasA.ytunnus.localeCompare(asiakasB.ytunnus, undefined, { numeric: true })
            }
          })
          return result
        }),
      )
      .subscribe(ilmoitukset => {
        this.dataSource.data = ilmoitukset
        this._ladataanService.lopetaLataaminen()
      })

    asetaArvo(status, this.lahetyksenTila[0].status)
  }

  ngAfterViewInit() {
    this.dataSource.sort = this._sort
    if (this.dataSource.paginator) {
      this.dataSource.paginator.pageSize = 50000
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.vuosikk, normalizedMonth)
    datepicker.close()
  }

  chosenMonthHandlerStart(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.hakulomake.start, normalizedMonth)
    datepicker.close()
  }

  chosenMonthHandlerEnd(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.hakulomake.end, normalizedMonth)
    datepicker.close()
  }

  lataa() {
    if (!this.lomake().valid()) {
      return
    }

    const data: YhteisomyyntilaskujenLataaminenDialogData = {
      kuukausi: this.kuukausi().getMonth() + 1,
      vuosi: this.kuukausi().getFullYear(),
    }

    this._dialog.open(YhteisomyyntilaskujenLataaminenDialog, {
      data: data,
    })
  }

  private _csvLatausKaynnissa = false
  async lataaCsv() {
    if (!this.lomake().valid()) {
      return
    }

    if (this._csvLatausKaynnissa) {
      return
    }
    this._csvLatausKaynnissa = true

    try {
      this._ladataanService.aloitaLataaminen()

      const data: YhteisomyyntilaskujenListauspyynto = {
        kuukausi: this.kuukausi().getMonth() + 1,
        vuosi: this.kuukausi().getFullYear(),
      }

      const vastaus = await this._firebaseLemonator.functionsCall<YhteisomyyntilaskujenListauspyynto, YhteisomyyntilaskujenCsvVastaus>('integraatiotOmaveroYhteisomyyntiCsv', data)
      const filename = 'Yhteisömyyntilaskut-CSV-' + data.vuosi + '-' + data.kuukausi + '.csv'
      this._fileSaverService.saveStringAs(vastaus.csv, filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._csvLatausKaynnissa = false
    }
  }
}
